import { Box } from "@mui/material";
import { useId, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { DocumentIconProps } from "../types";

/**
 * PowerPoint icon with Microsoft's official gradient design
 * Features complex gradients and professional styling
 * Three sizes: small (44px), medium (48px), large (80px)
 */
export const PowerPointIcon = ({ size, className }: DocumentIconProps) => {
  const { t } = useTranslation();
  const dimensions = {
    small: 44,
    medium: 48,
    large: 80,
  };

  const iconSize = dimensions[size];
  const uniqueId = useId();

  // Generate unique gradient IDs to prevent DOM conflicts when multiple icons are rendered
  // Using React's useId() for SSR-safe, collision-free IDs
  const gradientPrefix = useMemo(() => `ppt-${uniqueId}`, [uniqueId]);

  return (
    <Box
      component="svg"
      role="img"
      aria-label={t("translation.icons.powerpointAlt")}
      focusable={false}
      className={className}
      width={iconSize}
      height={iconSize}
      viewBox="60 78.75 581.25 562.5"
      xmlns="http://www.w3.org/2000/svg"
      xmlnsXlink="http://www.w3.org/1999/xlink"
      sx={{
        display: "inline-block",
        verticalAlign: "middle",
      }}
    >
      <title>{t("translation.icons.powerpointAlt")}</title>
      <defs>
        <linearGradient
          id={`${gradientPrefix}-linear0`}
          gradientUnits="userSpaceOnUse"
          x1="22.096001"
          y1="4.05626"
          x2="-0.876391"
          y2="26.0329"
          gradientTransform="matrix(15,0,0,15,0,0)"
        >
          <stop
            offset="0.0582736"
            style={{
              stopColor: "rgb(100%,49.803922%,28.235294%)",
              stopOpacity: 1,
            }}
          />
          <stop
            offset="1"
            style={{
              stopColor: "rgb(89.803922%,28.627451%,35.686275%)",
              stopOpacity: 1,
            }}
          />
        </linearGradient>
        <radialGradient
          id={`${gradientPrefix}-radial0`}
          gradientUnits="userSpaceOnUse"
          cx="0"
          cy="0"
          fx="0"
          fy="0"
          r="1"
          gradientTransform="matrix(-399.288723,399.288723,-672.056545,-672.056545,471.141014,241.960487)"
        >
          <stop
            offset="0.151523"
            style={{
              stopColor: "rgb(66.666667%,11.372549%,17.647059%)",
              stopOpacity: 1,
            }}
          />
          <stop
            offset="0.380855"
            style={{
              stopColor: "rgb(81.960784%,16.862745%,9.411765%)",
              stopOpacity: 0.439216,
            }}
          />
          <stop
            offset="0.601996"
            style={{ stopColor: "rgb(100%,23.529412%,0%)", stopOpacity: 0 }}
          />
        </radialGradient>
        <radialGradient
          id={`${gradientPrefix}-radial1`}
          gradientUnits="userSpaceOnUse"
          cx="0"
          cy="0"
          fx="0"
          fy="0"
          r="1"
          gradientTransform="matrix(484.012072,-228.617844,414.174468,876.858248,-19.41075,588.617992)"
        >
          <stop
            offset="0.4067"
            style={{
              stopColor: "rgb(100%,40%,98.431373%)",
              stopOpacity: 0.501961,
            }}
          />
          <stop
            offset="1"
            style={{
              stopColor: "rgb(91.764706%,23.921569%,0.392157%)",
              stopOpacity: 0,
            }}
          />
        </radialGradient>
        <linearGradient
          id={`${gradientPrefix}-linear1`}
          gradientUnits="userSpaceOnUse"
          x1="27.549299"
          y1="28.1723"
          x2="47.729"
          y2="13.2157"
          gradientTransform="matrix(15,0,0,15,0,0)"
        >
          <stop
            offset="0.310768"
            style={{
              stopColor: "rgb(100%,43.137255%,18.823529%)",
              stopOpacity: 1,
            }}
          />
          <stop
            offset="0.634576"
            style={{
              stopColor: "rgb(100%,62.745098%,36.078431%)",
              stopOpacity: 1,
            }}
          />
        </linearGradient>
        <radialGradient
          id={`${gradientPrefix}-radial2`}
          gradientUnits="userSpaceOnUse"
          cx="0"
          cy="0"
          fx="0"
          fy="0"
          r="1"
          gradientTransform="matrix(355.857605,74.568775,-71.089695,339.254713,312.756014,393.631496)"
        >
          <stop
            offset="0.78593"
            style={{
              stopColor: "rgb(100%,62.745098%,36.078431%)",
              stopOpacity: 0,
            }}
          />
          <stop
            offset="0.904889"
            style={{
              stopColor: "rgb(100%,80.784314%,51.764706%)",
              stopOpacity: 1,
            }}
          />
        </radialGradient>
        <radialGradient
          id={`${gradientPrefix}-radial3`}
          gradientUnits="userSpaceOnUse"
          cx="0"
          cy="0"
          fx="0"
          fy="0"
          r="1"
          gradientTransform="matrix(307.211443,-201.015927,192.233827,293.789814,369.794998,355.780506)"
        >
          <stop
            offset="0.295239"
            style={{ stopColor: "rgb(100%,60%,91.372549%)", stopOpacity: 0.8 }}
          />
          <stop
            offset="0.727968"
            style={{ stopColor: "rgb(100%,60%,91.372549%)", stopOpacity: 0 }}
          />
        </radialGradient>
        <radialGradient
          id={`${gradientPrefix}-radial4`}
          gradientUnits="userSpaceOnUse"
          cx="0"
          cy="0"
          fx="0"
          fy="0"
          r="1"
          gradientTransform="matrix(257.143156,-294.39511,268.864458,234.843083,328.567486,398.717995)"
        >
          <stop
            offset="0"
            style={{
              stopColor: "rgb(99.215686%,43.137255%,97.647059%)",
              stopOpacity: 1,
            }}
          />
          <stop
            offset="0.637205"
            style={{ stopColor: "rgb(100%,60%,26.666667%)", stopOpacity: 1 }}
          />
          <stop
            offset="0.85186"
            style={{
              stopColor: "rgb(98.823529%,76.862745%,47.45098%)",
              stopOpacity: 1,
            }}
          />
        </radialGradient>
        <radialGradient
          id={`${gradientPrefix}-radial5`}
          gradientUnits="userSpaceOnUse"
          cx="0"
          cy="0"
          fx="0"
          fy="0"
          r="1"
          gradientTransform="matrix(-29.045837,196.819309,-444.814842,-65.644064,302.984991,115.919852)"
        >
          <stop
            offset="0.144283"
            style={{
              stopColor: "rgb(100%,55.294118%,7.45098%)",
              stopOpacity: 1,
            }}
          />
          <stop
            offset="0.537266"
            style={{
              stopColor: "rgb(100%,49.803922%,16.078431%)",
              stopOpacity: 0,
            }}
          />
        </radialGradient>
        <radialGradient
          id={`${gradientPrefix}-radial6`}
          gradientUnits="userSpaceOnUse"
          cx="0"
          cy="0"
          fx="0"
          fy="0"
          r="1"
          gradientTransform="matrix(239.999814,239.999814,-239.999814,239.999814,59.97075,345)"
        >
          <stop
            offset="0"
            style={{
              stopColor: "rgb(97.254902%,9.803922%,24.313725%)",
              stopOpacity: 1,
            }}
          />
          <stop
            offset="0.939062"
            style={{
              stopColor: "rgb(57.254902%,2.352941%,8.627451%)",
              stopOpacity: 1,
            }}
          />
        </radialGradient>
        <radialGradient
          id={`${gradientPrefix}-radial7`}
          gradientUnits="userSpaceOnUse"
          cx="0"
          cy="0"
          fx="0"
          fy="0"
          r="1"
          gradientTransform="matrix(0.000000000000010287,167.999997,-191.25,0.000000000000011711,179.970002,488.999977)"
        >
          <stop
            offset="0.575893"
            style={{
              stopColor: "rgb(100%,69.019608%,33.333333%)",
              stopOpacity: 0,
            }}
          />
          <stop
            offset="0.973806"
            style={{
              stopColor: "rgb(100%,94.901961%,74.509804%)",
              stopOpacity: 0.301961,
            }}
          />
        </radialGradient>
      </defs>
      <g id="surface1">
        <path
          style={{ stroke: "none", fillRule: "nonzero" }}
          fill={`url(#${gradientPrefix}-linear0)`}
          d="M 641.199219 360 C 641.199219 204.667969 515.292969 78.75 359.976562 78.75 C 204.660156 78.75 78.75 204.667969 78.75 360 C 78.75 515.332031 204.660156 641.25 359.976562 641.25 C 515.292969 641.25 641.199219 515.332031 641.199219 360 Z M 641.199219 360 "
        />
        <path
          style={{ stroke: "none", fillRule: "nonzero" }}
          fill={`url(#${gradientPrefix}-radial0)`}
          d="M 641.199219 360 C 641.199219 204.667969 515.292969 78.75 359.976562 78.75 C 204.660156 78.75 78.75 204.667969 78.75 360 C 78.75 515.332031 204.660156 641.25 359.976562 641.25 C 515.292969 641.25 641.199219 515.332031 641.199219 360 Z M 641.199219 360 "
        />
        <path
          style={{ stroke: "none", fillRule: "nonzero" }}
          fill={`url(#${gradientPrefix}-radial1)`}
          d="M 641.199219 360 C 641.199219 204.667969 515.292969 78.75 359.976562 78.75 C 204.660156 78.75 78.75 204.667969 78.75 360 C 78.75 515.332031 204.660156 641.25 359.976562 641.25 C 515.292969 641.25 641.199219 515.332031 641.199219 360 Z M 641.199219 360 "
        />
        <path
          style={{ stroke: "none", fillRule: "nonzero" }}
          fill={`url(#${gradientPrefix}-linear1)`}
          d="M 360.015625 78.75 C 515.328125 78.753906 641.234375 204.671875 641.234375 360 C 641.234375 411.671875 627.273438 460.070312 602.960938 501.679688 L 607.53125 491.558594 C 635.363281 429.761719 590.125 359.832031 522.347656 359.882812 L 410.417969 359.96875 C 382.59375 359.992188 360.015625 337.433594 360.015625 309.609375 L 360.015625 197.476562 C 360.011719 129.671875 290.003906 84.484375 228.222656 112.410156 L 219.226562 116.484375 C 260.632812 92.492188 308.71875 78.75 360.015625 78.75 Z M 360.015625 78.75 "
        />
        <path
          style={{ stroke: "none", fillRule: "nonzero" }}
          fill={`url(#${gradientPrefix}-radial2)`}
          d="M 360.015625 78.75 C 515.328125 78.753906 641.234375 204.671875 641.234375 360 C 641.234375 411.671875 627.273438 460.070312 602.960938 501.679688 L 607.53125 491.558594 C 635.363281 429.761719 590.125 359.832031 522.347656 359.882812 L 410.417969 359.96875 C 382.59375 359.992188 360.015625 337.433594 360.015625 309.609375 L 360.015625 197.476562 C 360.011719 129.671875 290.003906 84.484375 228.222656 112.410156 L 219.226562 116.484375 C 260.632812 92.492188 308.71875 78.75 360.015625 78.75 Z M 360.015625 78.75 "
        />
        <path
          style={{ stroke: "none", fillRule: "nonzero" }}
          fill={`url(#${gradientPrefix}-radial3)`}
          d="M 360.015625 78.75 C 515.328125 78.753906 641.234375 204.671875 641.234375 360 C 641.234375 411.671875 627.273438 460.070312 602.960938 501.679688 L 607.53125 491.558594 C 635.363281 429.761719 590.125 359.832031 522.347656 359.882812 L 410.417969 359.96875 C 382.59375 359.992188 360.015625 337.433594 360.015625 309.609375 L 360.015625 197.476562 C 360.011719 129.671875 290.003906 84.484375 228.222656 112.410156 L 219.226562 116.484375 C 260.632812 92.492188 308.71875 78.75 360.015625 78.75 Z M 360.015625 78.75 "
        />
        <path
          style={{ stroke: "none", fillRule: "nonzero" }}
          fill={`url(#${gradientPrefix}-radial4)`}
          d="M 360.015625 78.75 C 515.328125 78.753906 641.234375 204.671875 641.234375 360 C 641.234375 411.671875 627.273438 460.070312 602.960938 501.679688 L 607.53125 491.558594 C 635.363281 429.761719 590.125 359.832031 522.347656 359.882812 L 410.417969 359.96875 C 382.59375 359.992188 360.015625 337.433594 360.015625 309.609375 L 360.015625 197.476562 C 360.011719 129.671875 290.003906 84.484375 228.222656 112.410156 L 219.226562 116.484375 C 260.632812 92.492188 308.71875 78.75 360.015625 78.75 Z M 360.015625 78.75 "
        />
        <path
          style={{ stroke: "none", fillRule: "nonzero" }}
          fill={`url(#${gradientPrefix}-radial5)`}
          d="M 360.015625 78.75 C 515.328125 78.753906 641.234375 204.671875 641.234375 360 C 641.234375 411.671875 627.273438 460.070312 602.960938 501.679688 L 607.53125 491.558594 C 635.363281 429.761719 590.125 359.832031 522.347656 359.882812 L 410.417969 359.96875 C 382.59375 359.992188 360.015625 337.433594 360.015625 309.609375 L 360.015625 197.476562 C 360.011719 129.671875 290.003906 84.484375 228.222656 112.410156 L 219.226562 116.484375 C 260.632812 92.492188 308.71875 78.75 360.015625 78.75 Z M 360.015625 78.75 "
        />
        <path
          style={{ stroke: "none", fillRule: "nonzero" }}
          fill={`url(#${gradientPrefix}-radial6)`}
          d="M 108.75 345 L 251.25 345 C 278.175781 345 300 366.824219 300 393.75 L 300 536.25 C 300 563.175781 278.175781 585 251.25 585 L 108.75 585 C 81.824219 585 60 563.175781 60 536.25 L 60 393.75 C 60 366.824219 81.824219 345 108.75 345 Z M 108.75 345 "
        />
        <path
          style={{ stroke: "none", fillRule: "nonzero" }}
          fill={`url(#${gradientPrefix}-radial7)`}
          d="M 108.75 345 L 251.25 345 C 278.175781 345 300 366.824219 300 393.75 L 300 536.25 C 300 563.175781 278.175781 585 251.25 585 L 108.75 585 C 81.824219 585 60 563.175781 60 536.25 L 60 393.75 C 60 366.824219 81.824219 345 108.75 345 Z M 108.75 345 "
        />
        <path
          style={{ stroke: "none", fillRule: "nonzero", fillOpacity: 1 }}
          fill="rgb(100%,100%,100%)"
          d="M 168.292969 488.90625 L 168.292969 533.570312 L 137.417969 533.570312 L 137.417969 396.425781 L 185.117188 396.425781 C 202.195312 396.425781 215.195312 400.15625 224.117188 407.617188 C 233.105469 415.074219 237.597656 426.136719 237.597656 440.800781 C 237.597656 455.914062 232.5625 467.707031 222.492188 476.1875 C 212.488281 484.667969 199.039062 488.90625 182.152344 488.90625 Z M 168.292969 420.144531 L 168.292969 465.1875 L 181.199219 465.1875 C 188.84375 465.1875 194.742188 463.183594 198.882812 459.164062 C 203.023438 455.148438 205.097656 449.378906 205.097656 441.855469 C 205.097656 434.90625 203.054688 429.550781 198.976562 425.785156 C 194.960938 422.023438 189.195312 420.144531 181.675781 420.144531 Z M 168.292969 420.144531 "
        />
      </g>
    </Box>
  );
};
